import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';

import ResearchHubPage from '../ResearchHubPage';
import AutoScrollSidebar from './Sidebar';

const Main = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Handle escape key to close sidebar
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && sidebarOpen) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [sidebarOpen]);

  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Header */}
      <header className='px-4 sm:px-6 lg:px-8 pt-4 lg:pt-8 pb-4 relative z-10'>
        <div className='max-w-7xl mx-auto'>
          <div className='text-center mb-4 lg:mb-8'>
            {/* Mobile Menu Button */}
            <div className="flex items-center justify-between lg:justify-center mb-4 lg:mb-0">
              <button
                onClick={toggleSidebar}
                className={`
                  lg:hidden p-3 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-lg
                  ${sidebarOpen 
                    ? 'text-white bg-blue-600 hover:bg-blue-700' 
                    : 'text-gray-700 bg-white hover:bg-gray-50 border border-gray-200'
                  }
                `}
                aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
                style={{ zIndex: 60 }} // Ensure button is always on top
              >
                {sidebarOpen ? (
                  <X size={24} className="transform transition-transform duration-200" />
                ) : (
                  <Menu size={24} className="transform transition-transform duration-200" />
                )}
              </button>
              
              <h1 className='text-xl sm:text-2xl lg:text-4xl xl:text-5xl font-extrabold text-gray-900 truncate mx-2'>
                Research Hub
              </h1>
              <div className="w-10 lg:hidden" />
            </div>
            
            <p className='text-sm sm:text-base lg:text-xl text-gray-600 max-w-2xl mx-auto px-4 mt-2'>
              Connecting researchers, academics, and industry professionals
              through premier scientific conferences
            </p>
          </div>
        </div>
      </header>

      {/* Main Layout Container */}
      <div className="relative">
        {/* Desktop Layout */}
        <div className="hidden lg:flex min-h-[calc(100vh-200px)]">
          {/* Desktop Sidebar - Always Visible */}
          <div className="flex-shrink-0">
            <AutoScrollSidebar 
              isOpen={true} // Always open on desktop
              onClose={() => {}} // No-op on desktop
            />
          </div>
          
          {/* Desktop Content */}
          <div className="flex-1 bg-white ml-4 rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <ResearchHubPage />
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="lg:hidden">
          {/* Mobile Content */}
          <div className="bg-white min-h-[calc(100vh-200px)]">
            <ResearchHubPage />
          </div>

          {/* Mobile Sidebar Overlay */}
          {sidebarOpen && (
            <>
              {/* Dark Backdrop */}
              <div 
                className="fixed inset-0 bg-black bg-opacity-30 z-40"
                style={{ top: 0 }} // Start from very top
                onClick={closeSidebar}
              />
              
              {/* Mobile Sidebar */}
              <div className="fixed inset-y-0 left-0 z-50 w-80 max-w-sm">
                <AutoScrollSidebar 
                  isOpen={sidebarOpen}
                  onClose={closeSidebar}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Status Indicator (for debugging) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 right-4 bg-black bg-opacity-75 text-white text-xs px-3 py-1 rounded-full z-50">
          Sidebar: {sidebarOpen ? 'Open' : 'Closed'}
        </div>
      )}

      {/* Global Styles */}
      <style jsx global>{`
        /* Ensure no body scroll when sidebar is open on mobile */
        ${sidebarOpen ? `
          @media (max-width: 1024px) {
            body {
              overflow: hidden;
            }
          }
        ` : ''}
        
        /* Smooth transitions for all sidebar elements */
        .sidebar-transition {
          transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
        }

        /* Prevent page jumping when button is clicked */
        button[aria-label*="sidebar"] {
          -webkit-tap-highlight-color: transparent;
          outline: none;
        }
      `}</style>
    </div>
  );
};

export default Main;